import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useSupplierAuth } from '../../context/SupplierAuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Building2, 
  FileText, 
  Upload, 
  AlertTriangle, 
  CheckCircle, 
  Clock,
  LogOut,
  User,
  Settings
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SupplierDashboard = () => {
  const { supplier, token, logout } = useSupplierAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDocuments = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/supplier-portal/documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const getDocumentStatus = (doc) => {
    if (!doc.expiry_date) return { status: 'no_expiry', label: 'No Expiry', color: 'status-inactive', icon: CheckCircle };
    
    const expiry = new Date(doc.expiry_date);
    const now = new Date();
    const daysUntilExpiry = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiry < 0) {
      return { status: 'expired', label: 'Expired', color: 'status-danger', icon: AlertTriangle };
    } else if (daysUntilExpiry <= 30) {
      return { status: 'expiring', label: `Expires in ${daysUntilExpiry} days`, color: 'status-warning', icon: Clock };
    }
    return { status: 'valid', label: 'Valid', color: 'status-active', icon: CheckCircle };
  };

  const expiredDocs = documents.filter(d => getDocumentStatus(d).status === 'expired');
  const expiringDocs = documents.filter(d => getDocumentStatus(d).status === 'expiring');
  const validDocs = documents.filter(d => ['valid', 'no_expiry'].includes(getDocumentStatus(d).status));

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h1 className="font-heading font-bold">NECOS.FM</h1>
              <p className="text-xs text-muted-foreground">Supplier Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/supplier-portal/profile">
              <Button variant="ghost" size="sm">
                <User className="w-4 h-4 mr-2" />
                Profile
              </Button>
            </Link>
            <Link to="/supplier-portal/documents">
              <Button variant="ghost" size="sm">
                <FileText className="w-4 h-4 mr-2" />
                Documents
              </Button>
            </Link>
            <Button variant="ghost" size="sm" onClick={logout}>
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="text-2xl font-heading font-bold">
            Welcome, {supplier?.company_name}
          </h2>
          <p className="text-muted-foreground mt-1">
            Manage your company profile and compliance documents
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Documents</p>
                  <p className="text-2xl font-bold">{documents.length}</p>
                </div>
                <FileText className="w-8 h-8 text-primary opacity-20" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Valid</p>
                  <p className="text-2xl font-bold text-green-600">{validDocs.length}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-600 opacity-20" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Expiring Soon</p>
                  <p className="text-2xl font-bold text-orange-600">{expiringDocs.length}</p>
                </div>
                <Clock className="w-8 h-8 text-orange-600 opacity-20" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Expired</p>
                  <p className="text-2xl font-bold text-red-600">{expiredDocs.length}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-red-600 opacity-20" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Alerts */}
        {(expiredDocs.length > 0 || expiringDocs.length > 0) && (
          <Card className="mb-8 border-orange-500/50 bg-orange-500/5">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2 text-orange-600">
                <AlertTriangle className="w-5 h-5" />
                Action Required
              </CardTitle>
            </CardHeader>
            <CardContent>
              {expiredDocs.length > 0 && (
                <div className="mb-4">
                  <p className="font-medium text-red-600 mb-2">
                    {expiredDocs.length} document(s) have expired:
                  </p>
                  <ul className="list-disc list-inside text-sm text-muted-foreground">
                    {expiredDocs.map(doc => (
                      <li key={doc.document_id}>{doc.original_filename}</li>
                    ))}
                  </ul>
                </div>
              )}
              {expiringDocs.length > 0 && (
                <div>
                  <p className="font-medium text-orange-600 mb-2">
                    {expiringDocs.length} document(s) expiring within 30 days:
                  </p>
                  <ul className="list-disc list-inside text-sm text-muted-foreground">
                    {expiringDocs.map(doc => (
                      <li key={doc.document_id}>
                        {doc.original_filename} - {getDocumentStatus(doc).label}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <Link to="/supplier-portal/documents">
                <Button className="mt-4">
                  <Upload className="w-4 h-4 mr-2" />
                  Upload New Documents
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="w-5 h-5 text-primary" />
                Company Profile
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <p><span className="text-muted-foreground">Contact:</span> {supplier?.contact_person}</p>
                <p><span className="text-muted-foreground">Email:</span> {supplier?.email}</p>
                <p><span className="text-muted-foreground">Phone:</span> {supplier?.phone}</p>
                <p><span className="text-muted-foreground">Status:</span> 
                  <Badge className="ml-2 capitalize">{supplier?.status?.replace('_', ' ')}</Badge>
                </p>
              </div>
              <Link to="/supplier-portal/profile">
                <Button variant="outline" className="mt-4 w-full">
                  <Settings className="w-4 h-4 mr-2" />
                  Edit Profile
                </Button>
              </Link>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                Recent Documents
              </CardTitle>
            </CardHeader>
            <CardContent>
              {documents.length === 0 ? (
                <p className="text-muted-foreground text-sm">No documents uploaded yet.</p>
              ) : (
                <div className="space-y-2">
                  {documents.slice(0, 5).map(doc => {
                    const docStatus = getDocumentStatus(doc);
                    const StatusIcon = docStatus.icon;
                    return (
                      <div key={doc.document_id} className="flex items-center justify-between text-sm">
                        <span className="truncate flex-1">{doc.original_filename}</span>
                        <Badge className={docStatus.color}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {docStatus.status === 'no_expiry' ? 'Valid' : docStatus.label}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              )}
              <Link to="/supplier-portal/documents">
                <Button variant="outline" className="mt-4 w-full">
                  <Upload className="w-4 h-4 mr-2" />
                  Manage Documents
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default SupplierDashboard;
